import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { REVIEW_STATUS, type ReviewStatus } from '@/constants/reviewStatus';
import { teacherDetailSchema } from '@/features/teacherSubmissions/types/teacherSubmission.types';
import {
  optionalDateSchema,
  optionalPositiveIdSchema,
  optionalTextSchema,
} from '@/utils/searchParams';
import { TEACHER_INACTIVATION_REASON } from '../constants/teacherInactivation.constants';

export const teacherInactivationRequestSchema = z.object({
  id: z.number(),
  teacherId: z.number(),
  teacherName: z.string(),
  teacherMobile: z.string().nullable(),
  teacherDesignation: z.string().nullable(),
  instituteName: z.string(),
  instituteCode: z.string().nullable(),
  eiinNumber: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  requestedBy: z.number(),
  requesterName: z.string(),
  requesterHrEmpId: z.string().nullable(),
  amName: z.string().nullable(),
  reasonCode: z.enum(TEACHER_INACTIVATION_REASON),
  reasonComment: z.string(),
  status: z.enum(REVIEW_STATUS),
  reviewedBy: z.number().nullable(),
  reviewerName: z.string().nullable(),
  reviewerComment: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const teacherInactivationDetailSchema = teacherInactivationRequestSchema.extend({
  teacher: teacherDetailSchema.nullable(),
});

export const paginatedTeacherInactivationRequestsSchema = z.object({
  data: z.array(teacherInactivationRequestSchema),
  meta: paginationMetaSchema,
});

export const teacherInactivationSummarySchema = z.object({
  total: z.number(),
  pending: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

export const teacherInactivationSearchSchema = z.object({
  search: optionalTextSchema,
  reviewStatus: z.enum(REVIEW_STATUS).optional().catch(undefined),
  reasonCode: z.enum(TEACHER_INACTIVATION_REASON).optional().catch(undefined),
  districtId: optionalPositiveIdSchema,
  thanaId: optionalPositiveIdSchema,
  amUserId: optionalPositiveIdSchema,
  zmUserId: optionalPositiveIdSchema,
  dateFrom: optionalDateSchema,
  dateTo: optionalDateSchema,
});

export type TeacherInactivationRequest = z.infer<typeof teacherInactivationRequestSchema>;
export type TeacherInactivationDetail = z.infer<typeof teacherInactivationDetailSchema>;
export type PaginatedTeacherInactivationRequests = z.infer<
  typeof paginatedTeacherInactivationRequestsSchema
>;
export type TeacherInactivationSummary = z.infer<typeof teacherInactivationSummarySchema>;
export type TeacherInactivationSearch = z.infer<typeof teacherInactivationSearchSchema>;
export type TeacherInactivationFilterParams = Omit<TeacherInactivationSearch, 'reviewStatus'>;

export interface TeacherInactivationListParams extends TeacherInactivationFilterParams {
  status?: ReviewStatus | undefined;
}
